import React from 'react';
import { Mail, Phone, AlertTriangle, ShieldCheck } from 'lucide-react';
import SEO from '../components/SEO';

const AmlPolicyPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="AML Policy | Anti-Money Laundering | Equi Pulse Research"
        description="Anti-Money Laundering (AML) and Know Your Customer (KYC) policy of Equi Pulse Research in compliance with PMLA and SEBI guidelines."
        url="/aml-policy"
      />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-amber-400 mb-4">Anti-Money Laundering (AML) Policy</h1>
        <p className="text-gray-400 mb-8">Policy for Prevention of Money Laundering and Know Your Client (KYC) Norms</p>
        
        <div className="prose prose-invert prose-amber max-w-none text-gray-300">
          {/* Company Details */}
          <div className="bg-slate-800/50 border border-amber-500/20 p-6 rounded-lg mb-8">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="w-8 h-8 text-emerald-400" aria-hidden="true" />
              <h2 className="text-2xl font-bold text-amber-400 m-0">Equi Pulse</h2>
            </div>
            <p className="mb-2"><strong>SEBI Registration No.:</strong> INH000024046</p>
            <p className="mb-2"><strong>Category:</strong> Research Analyst (Individual)</p>
            <p><strong>Registered Address:</strong> RE-54, Gyansila City, Dhabli, Indore (M.P.) – 453771</p>
          </div>

          {/* Introduction */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">1. Introduction</h2>
            <p className="mb-4">
              Equi Pulse is committed to the highest standards of Anti-Money Laundering (AML) compliance and requires all representatives, employees, and associates to adhere to these standards in preventing the use of our services for money laundering or terrorist financing activities.
            </p>
            <p>
              This policy is formulated in accordance with the Prevention of Money Laundering Act, 2002 (PMLA), SEBI (Research Analysts) Regulations, 2014, and applicable SEBI circulars and guidelines.
            </p>
          </div>

          {/* Objectives */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">2. Objectives</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Prevent money laundering and terrorist financing</li>
              <li>Comply with all applicable laws and regulatory guidelines</li>
              <li>Establish proper Know Your Client (KYC) procedures</li>
              <li>Ensure ongoing monitoring and reporting of suspicious activities</li>
              <li>Maintain proper records and documentation</li>
            </ul>
          </div>

          {/* KYC Procedures */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">3. Know Your Client (KYC) Procedures</h2>
            
            <div className="bg-emerald-900/20 border border-emerald-500/20 p-6 rounded-lg mb-4">
              <h3 className="text-xl font-semibold text-emerald-400 mb-3">3.1 Client Identification</h3>
              <p className="mb-3">Before providing any research services, Equi Pulse shall:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Verify the identity of the client through valid government-issued documents</li>
                <li>Obtain proof of address</li>
                <li>Obtain recent photograph of the client</li>
                <li>Verify PAN details</li>
                <li>For non-individual clients, obtain documents establishing legal status and authorized signatories</li>
              </ul>
            </div>

            <div className="bg-emerald-900/20 border border-emerald-500/20 p-6 rounded-lg mb-4">
              <h3 className="text-xl font-semibold text-emerald-400 mb-3">3.2 Acceptable Documents</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="font-semibold mb-2">Identity Proof:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm ml-2">
                    <li>PAN Card</li>
                    <li>Aadhaar Card</li>
                    <li>Passport</li>
                    <li>Voter ID</li>
                    <li>Driving License</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold mb-2">Address Proof:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm ml-2">
                    <li>Aadhaar Card</li>
                    <li>Passport</li>
                    <li>Utility Bills (not older than 3 months)</li>
                    <li>Bank Statement</li>
                    <li>Registered Lease/Rent Agreement</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-emerald-900/20 border border-emerald-500/20 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-emerald-400 mb-3">3.3 Risk-Based Approach</h3>
              <p>Equi Pulse adopts a risk-based approach where clients are categorized as:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-3">
                <li><strong>Low Risk:</strong> Individual resident Indian clients with standard KYC</li>
                <li><strong>Medium Risk:</strong> Non-resident Indians, HUFs, Partnership firms</li>
                <li><strong>High Risk:</strong> Politically Exposed Persons (PEPs), clients from high-risk jurisdictions</li>
              </ul>
            </div>
          </div>

          {/* Customer Due Diligence */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">4. Customer Due Diligence (CDD)</h2>
            <p className="mb-4">Enhanced due diligence is performed for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>High-value transactions</li>
              <li>Complex or unusual transactions</li>
              <li>Transactions involving high-risk clients or jurisdictions</li>
              <li>Politically Exposed Persons (PEPs)</li>
            </ul>
          </div>

          {/* Suspicious Transaction Reporting */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">5. Suspicious Transaction Reporting</h2>
            <div className="bg-amber-900/20 border border-amber-500/30 p-6 rounded-lg">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" aria-hidden="true" />
                <p>
                  Equi Pulse shall report suspicious transactions to the Financial Intelligence Unit-India (FIU-IND) as mandated under PMLA. A transaction is considered suspicious if:
                </p>
              </div>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>It appears unusual or inconsistent with the client's known profile</li>
                <li>The source of funds cannot be verified</li>
                <li>The client refuses to provide required information</li>
                <li>There are indications of potential money laundering or terrorist financing</li>
              </ul>
            </div>
          </div>

          {/* Record Keeping */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">6. Record Keeping</h2>
            <p className="mb-4">Equi Pulse shall maintain all records for a minimum period of 5 years from the date of cessation of services, including:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Client identification documents</li>
              <li>Transaction records</li>
              <li>Correspondence with clients</li>
              <li>Internal reports and analysis</li>
            </ul>
          </div>

          {/* Prohibited Activities */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">7. Prohibited Activities</h2>
            <div className="bg-red-900/20 border border-red-500/30 p-6 rounded-lg">
              <p className="mb-3 font-semibold text-red-400">Equi Pulse strictly prohibits:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Accepting cash payments</li>
                <li>Opening accounts in fictitious names or anonymous accounts</li>
                <li>Dealing with shell companies or entities with opaque ownership</li>
                <li>Any transaction that facilitates tax evasion or money laundering</li>
                <li>Providing services to clients who refuse to provide KYC documents</li>
              </ul>
            </div>
          </div>

          {/* Training & Awareness */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">8. Training & Awareness</h2>
            <p>
              Equi Pulse ensures that all personnel involved in client onboarding and service delivery are adequately trained on AML/KYC requirements and are aware of their obligations under applicable laws.
            </p>
          </div>

          {/* Contact Information */}
          <div className="bg-slate-800/50 border border-amber-500/20 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">9. Contact Information</h2>
            <p className="mb-4">For any queries regarding this AML Policy, please contact:</p>
            <div className="space-y-3">
              <p><strong>Compliance Officer:</strong> Siddharth Sharma</p>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-amber-400" aria-hidden="true" />
                <a href="mailto:compliance@equipulseresearch.com" className="text-amber-400 hover:text-amber-300">
                  compliance@equipulseresearch.com
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-amber-400" aria-hidden="true" />
                <a href="tel:+918982892301" className="text-amber-400 hover:text-amber-300">
                  +91-8982892301
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmlPolicyPage;
